import React, { useState } from 'react';
import 'twin.macro';
import Editor from '@teetsh/app/src/components/Editor/formWrapper';
import Button from 'components/Buttons/Button';
import { PlusIcon } from '@heroicons/react/20/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import _isEmpty from 'lodash-es/isEmpty';
import { useTranslation } from 'react-i18next';
import { EditorAIWrapperFF } from 'components/Editor/EditorAIWrapper';
import { useAIAssistCahierJournal } from '@teetsh/app/src/containers/CahierJournal/hooks/useAIAssistCahierJournal';
import { useGetAIAssistCJContextFromForm } from 'containers/CahierJournal/hooks/useGetAIAssistCJContextFromForm';
import { Attachment } from 'types/attachment';
import { useField } from 'react-final-form';
import { useUpdateCJEventAttachment } from 'containers/CahierJournal/queries/useUploadCJEventAttachment';
import useNotification from 'containers/App/Notifications/useNotification';
import { useCJConfigurationDisplay } from 'containers/CahierJournal/useCJConfigurationDisplay';

interface Props {
  value: string;
  onChange: (v: string) => void;
  hasAteliers: boolean;
}

export function Flow(props: Props) {
  const { hasAteliers, value, onChange } = props;
  const { t } = useTranslation(['cahierJournal', 'common']);
  const [hide, setHide] = useState(hasAteliers && _isEmpty(value));
  const [aiPausedForSession, setAIPausedForSession] = useState(false);

  const aiContext = useGetAIAssistCJContextFromForm();
  const aiHook = useAIAssistCahierJournal({ context: aiContext });
  const { notifySuccess, open: openNotification } = useNotification();
  const attachmentsField = useField<Attachment[]>('attachments');
  const { upload } = useUpdateCJEventAttachment();
  const { aiPaused, setAIPaused } = useCJConfigurationDisplay();

  async function handleAddAttachment(file: File) {
    const attachment = await upload({ file });
    if (attachment) {
      const currentAttachments = attachmentsField.input.value || [];
      attachmentsField.input.onChange([...currentAttachments, attachment]);
      notifySuccess({
        title: t('common:messages.attachmentAddedSuccess'),
      });
    }
  }

  function handlePauseUntilReactivate() {
    setAIPaused(true);
    openNotification({
      title: t('common:aiContextualActions.optionDisabled'),
      message: t('common:aiContextualActions.canReactivateInSettings'),
      intent: 'success',
      icon: InformationCircleIcon,
    });
  }

  if (hide) {
    return (
      <Button
        onClick={() => setHide(false)}
        fill
        alignText="left"
        tw="-mt-4" // hack : we want to compensate the parent flex gap
        icon={PlusIcon}
        small
        minimal
      >
        {t('ateliers.modal.addFlowToSeance')}
      </Button>
    );
  }

  return (
    <div>
      <h4 tw="text-neutral-moderate mb-2">{t('seance.modal.activity')}</h4>
      <div data-testid="deroulement">
        <EditorAIWrapperFF
          value={value}
          onChange={onChange}
          aiHook={aiHook}
          trackingContext="CahierJournal"
          onPdfAddAttachment={handleAddAttachment}
          onPauseForSession={() => setAIPausedForSession(true)}
          onPauseUntilReactivate={handlePauseUntilReactivate}
          hideSelectionToolbar={aiPaused || aiPausedForSession}
        >
          <Editor onChange={onChange} value={value} />
        </EditorAIWrapperFF>
      </div>
    </div>
  );
}
